import React, { createContext, useContext, useState, useEffect } from 'react';

// Create theme context
const ThemeContext = createContext();

// Theme provider component
export function ThemeProvider({ children }) {
  // Get initial theme from localStorage or default to light
  const getInitialTheme = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'light';
    }
    return 'light';
  };

  const [theme, setTheme] = useState(getInitialTheme);

  // Apply theme immediately
  const applyTheme = (currentTheme) => {
    const root = document.documentElement;

    // Set data-theme attribute for CSS selectors
    root.setAttribute('data-theme', currentTheme);

    if (currentTheme === 'dark') {
      // Dark theme colors
      root.style.setProperty('--bg-primary', '#1f2937'); // gray-800
      root.style.setProperty('--bg-secondary', '#374151'); // gray-700
      root.style.setProperty('--bg-card', '#4b5563'); // gray-600
      root.style.setProperty('--text-primary', '#f9fafb'); // gray-50
      root.style.setProperty('--text-secondary', '#d1d5db'); // gray-300
      root.style.setProperty('--border-color', '#6b7280'); // gray-500
      root.style.setProperty('--accent-yellow', '#fbbf24'); // yellow-400
      root.style.setProperty('--accent-yellow-hover', '#f59e0b'); // yellow-500
    } else {
      // Light theme colors (default)
      root.style.setProperty('--bg-primary', '#f9fafb'); // gray-50
      root.style.setProperty('--bg-secondary', '#ffffff'); // white
      root.style.setProperty('--bg-card', '#ffffff'); // white
      root.style.setProperty('--text-primary', '#111827'); // gray-900
      root.style.setProperty('--text-secondary', '#6b7280'); // gray-500
      root.style.setProperty('--border-color', '#d1d5db'); // gray-300
      root.style.setProperty('--accent-yellow', '#f59e0b'); // yellow-500
      root.style.setProperty('--accent-yellow-hover', '#d97706'); // yellow-600
    }

    // Save theme to localStorage
    localStorage.setItem('theme', currentTheme);
  };

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Apply initial theme immediately
  useEffect(() => {
    applyTheme(getInitialTheme());
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use theme context
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
